<template>
  <div class="sg">
    <div class="sg-section-head">
      <h2>General</h2>
      <p class="sg-help">
        Branding shown on the patient view, the default presentation layout TCs see when they
        open a plan, and basic plan-hygiene rules.
      </p>
    </div>

    <div class="sg-card">
      <h3 class="sg-card-title">Branding</h3>
      <div class="sg-field">
        <label>Practice name</label>
        <input
          v-model="local.practiceName"
          class="sg-input"
          :placeholder="practiceFallbackName || 'Your practice name'"
        />
        <p class="sg-hint">
          Shown at the top of every patient view. Leave blank to use your site name from Teamio
          ({{ practiceFallbackName || 'unset' }}).
        </p>
      </div>
      <div class="sg-field">
        <label>Tagline</label>
        <input
          v-model="local.practiceTagline"
          class="sg-input"
          placeholder="e.g., FAMILY DENTISTRY OF EMERSON"
        />
        <p class="sg-hint">Optional, displayed below the practice name in small uppercase.</p>
      </div>
    </div>

    <div class="sg-card">
      <h3 class="sg-card-title">Procedure labels</h3>
      <p class="sg-card-desc">
        Enabled patient views, default layout, and how prices display now live in the
        <strong>Presentation</strong> section.
      </p>
      <div class="sg-field">
        <label class="sg-switch-label">
          <span class="sg-switch">
            <input type="checkbox" v-model="local.plainLanguageEnabled" />
            <span class="sg-switch-slider"></span>
          </span>
          <span class="sg-switch-text">
            <strong>Plain-language labels</strong>
            <span class="sg-switch-sub">
              Use AI-generated short names for procedures (e.g., "Crown" instead of "All-ceramic crown D2740").
              Procedure Content section lets you edit individual codes (Slice 6).
            </span>
          </span>
        </label>
      </div>
    </div>

    <!-- Slice 12 — post-sign guided wizard toggle -->
    <div class="sg-card">
      <h3 class="sg-card-title">Patient guidance</h3>
      <div class="sg-field">
        <label class="sg-switch-label">
          <span class="sg-switch">
            <input type="checkbox" v-model="local.patientGuidedFlowEnabled" />
            <span class="sg-switch-slider"></span>
          </span>
          <span class="sg-switch-text">
            <strong>Guide patients after signing</strong>
            <span class="sg-switch-sub">
              After a patient signs their plan, walk them through the consent forms and payment
              in a step-by-step popup.
            </span>
          </span>
        </label>
      </div>
    </div>

    <!-- Slice 7d — OD imaging upload destination -->
    <div class="sg-card">
      <h3 class="sg-card-title">Open Dental imaging</h3>
      <div class="sg-field">
        <label>Imaging category for signed plans</label>
        <select
          v-model="local.imagingCategoryDefNum"
          class="sg-input"
          :disabled="loadingCategories || imagingCategories.length === 0"
        >
          <option :value="null">— Pick a category —</option>
          <option
            v-for="cat in imagingCategories"
            :key="cat.defNum"
            :value="cat.defNum"
          >{{ cat.itemName }}</option>
        </select>
        <p class="sg-hint" v-if="loadingCategories">Loading from Open Dental…</p>
        <p class="sg-hint" v-else-if="imagingCategories.length === 0">
          No imaging categories returned. Verify Open Dental is reachable for this practice.
        </p>
        <p class="sg-hint" v-else>
          When a patient signs their plan, the PDF is uploaded into this Open Dental imaging
          category for the patient. Pick the category your practice uses for treatment plans
          or signed forms.
        </p>
      </div>
    </div>

    <!-- 2026-09-09 — tick OD's "Signed" box on the plan. Sits beside the imaging
         card because both are "what we write back into Open Dental on signing", but
         they are INDEPENDENT: the signed PDF still files to imaging either way. -->
    <div class="sg-card">
      <h3 class="sg-card-title">Open Dental signature</h3>
      <div class="sg-field">
        <label class="sg-switch-label">
          <span class="sg-switch">
            <input type="checkbox" v-model="local.odSignatureWriteback" />
            <span class="sg-switch-slider"></span>
          </span>
          <span class="sg-switch-text">
            <strong>Mark plans as signed in Open Dental</strong>
            <span class="sg-switch-sub">
              When a patient signs, tick the Signed box on their treatment plan in Open Dental.
              This replaces any signature already on that plan there, including one captured
              inside Open Dental itself. A note is added to the patient's chart either way.
            </span>
          </span>
        </label>
      </div>
    </div>

    <!-- Slice 7d — Post-sign email template -->
    <div class="sg-card">
      <h3 class="sg-card-title">Post-sign email to patient</h3>
      <p class="sg-card-desc">
        After a patient signs their treatment plan, they get a confirmation email with the signed
        PDF attached (if they have an email on file with your office). Customize the message below.
        Available placeholders: <code>&#123;patientFirstName&#125;</code>, <code>&#123;practiceName&#125;</code>.
        Your Email Signature (Settings → Email) is added below this message automatically when it
        is switched on — don't paste it here.
      </p>
      <div class="sg-field">
        <label>Subject</label>
        <input
          v-model="local.postSignEmailSubject"
          class="sg-input"
          placeholder="Your treatment plan from {practiceName}"
        />
      </div>
      <div class="sg-field">
        <label>Body</label>
        <textarea
          v-model="local.postSignEmailBody"
          class="sg-input sg-textarea"
          rows="7"
          placeholder="Email body..."
        ></textarea>
      </div>
    </div>

    <!-- Office email address (2026-08-30).
         ⛔ RENDERED UNCONDITIONALLY, AND MUST STAY THAT WAY. No v-if, no :disabled,
         no lock class. This field is an INPUT to the site's email availability, so
         gating it on that availability creates a closed loop: until 2026-08-30 the
         address lived inside the comments `v-if` below, which is hidden precisely
         when a site has no working sender — so the practices that needed to enter
         an address were the only ones who could not see the box, and the SuperAdmin
         refusal told them to go to this very screen. Never gate an input on the
         availability it feeds. -->
    <div class="sg-card">
      <h3 class="sg-card-title">Office email address</h3>
      <p class="sg-card-desc">
        The email address your practice can be reached at — usually your front desk or main office
        inbox. When Teamio emails a treatment plan to a patient on your behalf, this is the address
        the patient sees and where their reply lands. Don't use a personal address unless you want
        patients replying to that person directly.
      </p>
      <div class="sg-field">
        <label>Where patient replies go</label>
        <input
          v-model="local.officeReplyEmail"
          class="sg-input"
          type="email"
          placeholder="frontdesk@yourpractice.com"
        />
        <p v-if="officeEmailHint" class="sg-hint">{{ officeEmailHint }}</p>
      </div>
      <p v-if="officeEmailMissing" class="sg-warn-note">
        <i class="fa fa-exclamation-triangle"></i>
        Until this is filled in, Teamio can't send emails to patients for your practice.
      </p>
    </div>

    <!-- Email signature (2026-09-01) — READ-ONLY mirror of the site-wide
         EMAIL_SIGNATURE setting. Deliberately introduces NO Tx Presenter setting:
         there is exactly ONE signature per practice, configured under
         Settings → Email, and every Teamio email that carries it (including all
         three Tx Presenter patient emails) appends the STORED html verbatim.
         Surfaced here only so the office can see what lands at the bottom of a
         treatment-plan email without leaving this screen. -->
    <div class="sg-card">
      <h3 class="sg-card-title">Email signature</h3>
      <p class="sg-card-desc">
        The block added to the bottom of your treatment-plan emails — logo, practice name, phone,
        address and links. Set up once for the whole practice under Settings → Email; Teamio adds
        it to all three treatment-plan emails automatically, and Wiki email templates can place it
        with the <code v-pre>{{email_signature_block}}</code> variable.
      </p>

      <p v-if="!emailSignatureConfigured" class="sg-hint">
        No signature is set up yet, so treatment-plan emails end with just the message body.
      </p>
      <template v-else>
        <p class="sg-hint">
          <template v-if="emailSignatureActive">
            Currently <strong>on</strong> — patients see this under your treatment-plan emails.
          </template>
          <template v-else>
            Set up, but currently <strong>off</strong> — nothing is added to your emails until
            it is switched back on.
          </template>
        </p>
        <!-- v-html of the STORED html. That string is produced by our own renderer
             (src/utils/emailSignature.js) at save time: every field is HTML-escaped
             and only http(s) URLs ever become an href/src. -->
        <div
          class="sg-sig-preview"
          :class="{ 'sg-sig-preview-off': !emailSignatureActive }"
          v-html="emailSignatureHtml"
        ></div>
      </template>

      <p class="sg-lock-note">
        <i class="fa fa-lock"></i>
        Read-only here. To change it, go to <strong>Settings &rarr; Email</strong> and edit the
        <strong>Email Signature</strong> card.
      </p>
    </div>

    <!-- MASTER toggle for patient comments / questions (2026-08-28).
         Default OFF. Greyed out entirely without a verified sender email —
         comments with no sender is a dead end in both directions (the office is
         never told a question arrived, the patient is never told there's an
         answer), and the server refuses to store `true` in that state. -->
    <div class="sg-card" :class="{ 'sg-card-locked': commentsLocked }">
      <h3 class="sg-card-title">Patient comments &amp; questions</h3>
      <p class="sg-card-desc">
        Lets patients ask questions right on their treatment plan page. Your team sees and answers
        them from the <strong>Comments</strong> panel in the plan Builder, and the patient gets a
        text or email when you reply. Leave this off and the plan page shows no conversation section
        at all — patients are pointed to call the office instead.
      </p>
      <label class="sg-switch-label">
        <span class="sg-switch">
          <!-- :checked + @change, deliberately NOT v-model: a practice that turned
               this on and later lost its verified sender must render UNCHECKED and
               disabled, matching what the server will now enforce — v-model would
               show a checked box the office cannot act on. -->
          <input
            type="checkbox"
            :checked="local.commentsEnabled && !commentsLocked"
            :disabled="commentsLocked"
            @change="local.commentsEnabled = $event.target.checked"
          />
          <span class="sg-switch-slider"></span>
        </span>
        <span class="sg-switch-text">
          <strong>Allow Comments / Questions on Treatment Plans</strong>
          <span class="sg-switch-sub">
            Turning this off hides the conversation from patients but keeps every past message —
            turn it back on and the history is there.
          </span>
        </span>
      </label>
      <!-- ⚠️ This note used to name ONLY Settings → Email. That is a dead end for a
           practice with no domain of its own — exactly the practices that hit it —
           so it has to offer the Teamio-sending route too. -->
      <p v-if="commentsLocked" class="sg-lock-note">
        <i class="fa fa-lock"></i>
        To enable patient comments, your practice needs a way to send email — either your own
        verified sender under Settings → Email, or Teamio's built-in sending. Fill in your office
        email address above, then ask Teamio support to turn on sending for your practice.
      </p>
    </div>

    <!-- AI replies and notifications are settings ABOUT patient comments, so they
         only make sense once the master toggle is on. Their saved values are left
         untouched in the blob while hidden. -->
    <template v-if="local.commentsEnabled && !commentsLocked">
    <!-- Slice 8b — AI Q&A on Comments thread -->
    <div class="sg-card">
      <h3 class="sg-card-title">AI replies on patient questions</h3>
      <p class="sg-card-desc">
        When patients ask questions on their treatment plan, an AI assistant trained on the plan
        can draft a reply (shown to the patient as "Office") within a few seconds. Your team can
        override or replace AI replies at any time. Disable this if you'd rather respond to every
        question manually.
      </p>
      <label class="sg-switch-label">
        <span class="sg-switch">
          <input type="checkbox" v-model="local.aiCommentsEnabled" />
          <span class="sg-switch-slider"></span>
        </span>
        <span class="sg-switch-text">
          <strong>Allow AI to draft replies to patient questions</strong>
          <span class="sg-switch-sub">
            Patients always see replies as "Office," with a small disclosure banner above the
            question box.
          </span>
        </span>
      </label>

      <!-- Slice 8d-1 — AI delivery mode. Only meaningful when aiCommentsEnabled
           is on; we keep the radio visible either way so the choice persists. -->
      <div class="sg-field" :class="{ 'sg-field-muted': !local.aiCommentsEnabled }">
        <label class="sg-radio-row sg-radio-row-stacked">
          <span class="sg-radio">
            <input type="radio" value="auto" v-model="local.aiReplyMode" />
            <span class="sg-radio-text">
              <strong>Send AI replies immediately</strong>
              <span class="sg-radio-sub">
                Patients see the AI reply within seconds of asking. You can still override an AI
                reply afterward — but only if the patient hasn't read it yet.
              </span>
            </span>
          </span>
        </label>
        <label class="sg-radio-row sg-radio-row-stacked">
          <span class="sg-radio">
            <input type="radio" value="review" v-model="local.aiReplyMode" />
            <span class="sg-radio-text">
              <strong>Hold AI replies for my review</strong>
              <span class="sg-radio-sub">
                AI drafts a reply but the patient doesn't see it until you approve or rewrite it
                in the Comments panel. Good for staged adoption while you build trust in the AI.
              </span>
            </span>
          </span>
        </label>
      </div>
    </div>

    <!-- Slice 8d — Patient comment notifications -->
    <div class="sg-card">
      <h3 class="sg-card-title">Patient comment notifications</h3>
      <p class="sg-card-desc">
        Email addresses that get notified when a patient asks a question on their treatment plan.
        "Whoever sees it first responds" — add the office's shared inbox or a few team members'
        addresses. Leave empty to skip office notifications.
      </p>
      <div class="sg-field">
        <label>Notify these emails</label>
        <div class="sg-chip-input" :class="{ 'sg-chip-input-focused': notifEmailFocused }">
          <span
            v-for="(addr, i) in (local.notificationEmails || [])"
            :key="addr + '-' + i"
            class="sg-chip"
          >
            {{ addr }}
            <button type="button" class="sg-chip-x" @click="onRemoveNotifEmail(i)" title="Remove">
              ✕
            </button>
          </span>
          <input
            v-model="notifEmailDraft"
            class="sg-chip-input-field"
            type="email"
            :placeholder="(local.notificationEmails || []).length === 0 ? 'team@yourpractice.com' : ''"
            @keydown.enter.prevent="onAddNotifEmail"
            @keydown.,.prevent="onAddNotifEmail"
            @blur="onAddNotifEmail"
            @focus="notifEmailFocused = true"
          />
        </div>
        <p class="sg-hint">Press Enter or comma to add. Patients will see replies as "Office," not from individual addresses.</p>
      </div>
      <!-- Warn, don't refuse: an empty list is a legitimate choice (a practice
           that watches the dashboard), but with the default "hold AI replies for
           my review" mode nobody is told a question is waiting, so a held draft
           can sit unseen. Say so rather than blocking the save. -->
      <p v-if="notifyEmailsEmpty" class="sg-warn-note">
        <i class="fa fa-exclamation-triangle"></i>
        With no addresses here, new patient questions are only visible in the Tx Presenter dashboard.
      </p>
    </div>
    </template>

    <!-- Client-side save refusal. The server refuses this too — this is the
         immediate, in-context version so the office isn't told "saved" and then
         corrected by a round trip. -->
    <p v-if="saveBlockedMsg" class="sg-warn-note">
      <i class="fa fa-exclamation-triangle"></i>
      {{ saveBlockedMsg }}
    </p>

    <!-- Footer: save bar -->
    <div class="sg-savebar">
      <span v-if="dirty" class="sg-dirty">Unsaved changes</span>
      <span v-else class="sg-clean">All changes saved</span>
      <span class="sg-spacer"></span>
      <button class="sg-btn" @click="onReset" :disabled="!dirty">Reset</button>
      <button class="sg-btn-primary" @click="onSave" :disabled="!dirty || saving">
        <i v-if="saving" class="fa fa-circle-o-notch fa-spin"></i>
        Save
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import mixin from '@/mixin.js';
import { TX_PRESENTER_DEFAULTS } from '@/pages/TxPresenter/constants/settingsDefaults.js';
import { resolveOfficeReplyEmail } from '@/pages/TxPresenter/utils/resolveChannels.js';
import { readSettingValue } from '@/utils/siteCapability.js';
import { parseSignatureSetting, isSignatureConfigured } from '@/utils/emailSignature.js';

/** The site-wide signature row. Read-only from here — Settings → Email owns the writes. */
const EMAIL_SIGNATURE_SETTING = 'EMAIL_SIGNATURE';

const cloneDefaults = () => JSON.parse(JSON.stringify(TX_PRESENTER_DEFAULTS.general));

export default {
  name: 'SettingsGeneral',
  mixins: [mixin],
  props: {
    loading: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    practiceFallbackName: { type: String, default: '' },
  },
  data() {
    return {
      local: cloneDefaults(),
      original: cloneDefaults(),
      imagingCategories: [],
      loadingCategories: false,
      // Client-side save refusal (the office email is required in platform mode).
      saveBlockedMsg: '',
      // Slice 8d — chip input for notificationEmails
      notifEmailDraft: '',
      notifEmailFocused: false,
    };
  },
  computed: {
    ...mapGetters('txPresenter', ['settingsGeneralResolved', 'siteChannels', 'siteChannelsReady']),
    dirty() {
      return JSON.stringify(this.local) !== JSON.stringify(this.original);
    },
    /**
     * No verified sender email ⇒ the patient-comments card is disabled and the
     * value is forced off on save. Mirrors SendPlanModal.vue's siteEmailAvailable
     * and the backend's resolveCommentsGate.
     *
     * Renders UNLOCKED while customer.settings is still hydrating
     * (siteChannelsReady false): "unknown" is not "denied", and a cold load
     * would otherwise flash a greyed-out card at a practice that has a sender
     * perfectly well. The server refuses the save either way.
     */
    commentsLocked() {
      return this.siteChannelsReady && !(this.siteChannels && this.siteChannels.email.available);
    },
    /** Comments on, but nobody gets told a question came in. Warned, not blocked. */
    notifyEmailsEmpty() {
      return !(this.local.notificationEmails || []).length;
    },

    /**
     * Is this site sending THROUGH TEAMIO? Both reasons mean "platform mode is
     * this site's mode" — 'platform-sender' is fully configured, and
     * 'platform-sender-not-configured' is the same mode missing a piece.
     */
    onPlatformSender() {
      if (!this.siteChannelsReady || !this.siteChannels) return false;
      const r = this.siteChannels.email.reason;
      return r === 'platform-sender' || r === 'platform-sender-not-configured';
    },

    /** The address the resolver would actually use — explicit key or legacy fallback. */
    effectiveOfficeEmail() {
      return resolveOfficeReplyEmail(this.local);
    },

    /** Platform mode with no address anywhere ⇒ the site cannot email at all. */
    officeEmailMissing() {
      return this.onPlatformSender && !this.effectiveOfficeEmail;
    },

    /**
     * A contextual note rather than a conditional field — the card itself always
     * renders (see the template comment), because hiding it is what caused the
     * deadlock. Three states worth explaining, otherwise silent.
     */
    officeEmailHint() {
      const explicit = String(this.local.officeReplyEmail || '').trim();
      const ownSender = this.siteChannelsReady && this.siteChannels
        && this.siteChannels.sender && !this.siteChannels.sender.isPlatform;
      if (ownSender) {
        return 'Your practice sends from its own verified email address, so patient replies already '
          + 'go there. This field is only used if that ever changes.';
      }
      if (!explicit && this.effectiveOfficeEmail) {
        // Legacy fallback is carrying this site. Nudge toward being explicit
        // rather than silently depending on the first chip in a different list.
        return `Currently using ${this.effectiveOfficeEmail} from your notification list. `
          + 'Enter it here to set it explicitly.';
      }
      return '';
    },

    // ── Email signature (read-only mirror) ───────────────────────────────────
    // Corrupt or absent row ⇒ null, never a throw: a bad blob must degrade to
    // "no signature", not break the whole settings screen.
    emailSignatureBlob() {
      return parseSignatureSetting(readSettingValue(this.settings, EMAIL_SIGNATURE_SETTING));
    },

    emailSignatureHtml() {
      const blob = this.emailSignatureBlob;
      return (blob && typeof blob.html === 'string') ? blob.html : '';
    },

    /** Something is saved and it renders — regardless of the on/off switch. */
    emailSignatureConfigured() {
      return this.emailSignatureHtml.trim() !== '';
    },

    /** What a backend consumer tests before appending: enabled AND non-empty html. */
    emailSignatureActive() {
      return isSignatureConfigured(this.emailSignatureBlob);
    },
  },
  watch: {
    settingsGeneralResolved: {
      immediate: true,
      handler(resolved) {
        if (!resolved) return;
        const merged = { ...cloneDefaults(), ...resolved };
        // Deep-merge enabledViews; coerce a legacy 'phase' default to 'region'.
        merged.enabledViews = { ...cloneDefaults().enabledViews, ...(resolved.enabledViews || {}) };
        if (merged.defaultView === 'phase') merged.defaultView = 'region';
        // Slice 12 — backfill guided-flow flag for General blobs saved before it
        // existed (undefined → default ON), so the toggle reflects the real
        // default instead of rendering an unchecked box.
        if (merged.patientGuidedFlowEnabled === undefined) merged.patientGuidedFlowEnabled = true;
        // Master comments toggle: strict boolean coercion, applied to BOTH local
        // and original (original is cloned from merged below) so a blob holding
        // the string 'true' or a 1 can't render as on here while the backend
        // gate — which also tests === true — reads it as off. Coercing only
        // `local` would also fabricate permanent unsaved-changes state.
        // No absent-key backfill: this feature defaults OFF, so undefined → false
        // is the correct answer, not a migration gap.
        merged.commentsEnabled = merged.commentsEnabled === true;
        // OD signature write-back: same strict coercion, same reasoning, and the
        // stakes are higher — the backend also tests === true, and the write it
        // guards replaces a real signature in Open Dental. Absent ⇒ false is the
        // correct answer here too: before 2026-09-09 no blob carried this key and
        // every site got the write, so backfilling it to true would silently
        // re-enable exactly what this toggle exists to stop.
        merged.odSignatureWriteback = merged.odSignatureWriteback === true;
        this.local = merged;
        this.original = JSON.parse(JSON.stringify(merged));
      },
    },
  },
  mounted() {
    this.loadImagingCategories();
  },
  methods: {
    // Slice 8d — chip input handlers for the notificationEmails free-form list
    onAddNotifEmail() {
      const raw = String(this.notifEmailDraft || '').trim().replace(/[,;]\s*$/, '');
      if (!raw) return;
      // Light validation: must contain @ and .
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) {
        this.notifEmailDraft = raw; // keep typed value so user can fix
        return;
      }
      const list = Array.isArray(this.local.notificationEmails) ? this.local.notificationEmails.slice() : [];
      const existing = new Set(list.map((e) => e.toLowerCase()));
      if (!existing.has(raw.toLowerCase())) {
        list.push(raw);
        this.local.notificationEmails = list;
      }
      this.notifEmailDraft = '';
    },
    onRemoveNotifEmail(index) {
      const list = Array.isArray(this.local.notificationEmails) ? this.local.notificationEmails.slice() : [];
      list.splice(index, 1);
      this.local.notificationEmails = list;
    },

    async loadImagingCategories() {
      if (!this.siteID) return;
      this.loadingCategories = true;
      try {
        const response = await axios.post('/get-tx-imaging-categories', { siteID: this.siteID }, { crossdomain: true });
        if (response.data && response.data.success) {
          this.imagingCategories = response.data.data.categories || [];
        }
      } catch (e) {
        console.warn('[SettingsGeneral] failed to load imaging categories:', e.message);
      } finally {
        this.loadingCategories = false;
      }
    },
    async onSave() {
      try {
        this.saveBlockedMsg = '';
        // ⛔ Refuse to save away the only Reply-To a platform-sender site has.
        // That address is what patients reply to; clearing it silently ends the
        // site's ability to email patients at all while the screen says "saved".
        // Refuse the config that can never fire, rather than store-and-report-success.
        // Never fires for own-sender sites — their availability comes from
        // GENERAL_VERIFIED_EMAILS and ignores this field entirely.
        if (this.onPlatformSender && !this.effectiveOfficeEmail) {
          this.saveBlockedMsg = 'Your practice sends patient email through Teamio, so an office '
            + 'email address is required — it is where patient replies go. Add one before saving.';
          return;
        }
        // Converge the stored blob on `false` when the sender email went away
        // after comments were enabled. The card renders unchecked + disabled in
        // that state, so saving `this.local` untouched would silently re-persist
        // a `true` the UI is not showing and the backend would 400 on anyway.
        const value = { ...this.local };
        if (this.commentsLocked) value.commentsEnabled = false;
        await this.$store.dispatch('txPresenter/saveSettingsSection', {
          section: 'general',
          value,
        });
        this.local = value;
        this.original = JSON.parse(JSON.stringify(value));
      } catch (e) { /* in state */ }
    },
    onReset() {
      this.local = JSON.parse(JSON.stringify(this.original));
    },
  },
};
</script>

<style scoped>
.sg {
  max-width: 720px;
  margin: 0 auto;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
}
.sg-section-head { margin-bottom: 22px; }
.sg-section-head h2 {
  margin: 0 0 4px;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.3px;
  color: #0f172a;
}
.sg-help {
  margin: 0;
  font-size: 13px;
  color: #64748b;
  line-height: 1.55;
}

.sg-card {
  background: #fff;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  padding: 20px 22px;
  margin-bottom: 16px;
  box-shadow: 0 1px 2px rgba(15,23,42,0.04);
}
.sg-card-title {
  margin: 0 0 14px;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: #475569;
}
.sg-card-desc {
  margin: -6px 0 14px;
  font-size: 12.5px;
  color: #64748b;
  line-height: 1.55;
}
/* No verified sender email — the whole patient-comments card is inert. The
   dimming is cosmetic only; the real stop is :disabled on the input (the
   existing .sg-field-muted { pointer-events: none } idiom does NOT stop
   keyboard toggling) plus the coercion in onSave and the server's 400. */
.sg-card-locked { opacity: 0.55; }
.sg-lock-note {
  margin: 10px 0 0;
  font-size: 11.5px;
  color: #94a3b8;
  line-height: 1.5;
}
.sg-lock-note .fa { margin-right: 5px; }
.sg-warn-note {
  margin: 10px 0 0;
  font-size: 11.5px;
  color: #b45309;
  line-height: 1.5;
}
.sg-warn-note .fa { margin-right: 5px; }
.sg-card-desc code {
  font-family: 'SFMono-Regular', Menlo, Monaco, monospace;
  font-size: 11.5px;
  background: #f1f3f6;
  padding: 1px 5px;
  border-radius: 3px;
  color: #0d5f54;
}
.sg-textarea {
  resize: vertical;
  min-height: 100px;
  line-height: 1.55;
  font-family: inherit;
}

.sg-field { margin-bottom: 16px; }
.sg-field:last-child { margin-bottom: 0; }
.sg-field > label {
  display: block;
  font-size: 12px;
  color: #475569;
  font-weight: 600;
  margin-bottom: 6px;
}

.sg-input,
.sg-input-num {
  display: block;
  width: 100%;
  padding: 8px 10px;
  font-size: 13px;
  border: 1px solid #e4e7ec;
  border-radius: 6px;
  background: #fff;
  color: #0f172a;
  font-family: inherit;
}
.sg-input:focus,
.sg-input-num:focus {
  outline: none;
  border-color: #0d5f54;
  box-shadow: 0 0 0 3px rgba(13, 95, 84, 0.12);
}
.sg-input-num { max-width: 120px; }

/* Native select rendering was clipping the selected value (white-on-white
   inside the browser's own widget). Force a clean custom dropdown with our
   own chevron so the text always shows. Targets <select class="sg-input">. */
select.sg-input {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  height: 36px;
  line-height: 1.4;
  padding: 0 30px 0 10px;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'><path fill='none' stroke='%23475569' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round' d='M3 4.5l3 3 3-3'/></svg>");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 12px 12px;
  cursor: pointer;
}
select.sg-input:disabled {
  background-color: #f1f3f6;
  color: #94a3b8;
  cursor: not-allowed;
}
.sg-hint {
  margin: 6px 0 0;
  font-size: 11.5px;
  color: #94a3b8;
  line-height: 1.5;
}

/* Read-only mini preview of the site-wide email signature. The inner markup
   carries its own inline styles (it is email HTML), so this only frames it. */
.sg-sig-preview {
  margin-top: 10px;
  padding: 12px 14px;
  border: 1px solid #e4e7ec;
  border-radius: 6px;
  background: #fbfcfd;
  overflow-x: auto;
}
.sg-sig-preview-off { opacity: 0.55; }

.sg-radio-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.sg-radio {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid #e4e7ec;
  border-radius: 999px;
  font-size: 12.5px;
  color: #1e293b;
  cursor: pointer;
  background: #fff;
}
.sg-radio input[type="radio"] {
  accent-color: #0d5f54;
  margin: 0;
}

/* Enabled-views checkboxes — same pill affordance as the radios. */
.sg-check-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.sg-check {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid #e4e7ec;
  border-radius: 999px;
  font-size: 12.5px;
  color: #1e293b;
  cursor: pointer;
  background: #fff;
}
.sg-check input[type="checkbox"] {
  accent-color: #0d5f54;
  margin: 0;
}
.sg-check input[type="checkbox"]:disabled {
  cursor: not-allowed;
}
.sg-check input[type="checkbox"]:disabled + span {
  color: #94a3b8;
}

/* Slice 8d-1 — stacked radio rows (replace pill style with full-width
   rows that have a heading + sub-line, matching the toggle pattern). */
.sg-radio-row-stacked {
  display: block;
  margin-top: 10px;
}
.sg-radio-row-stacked .sg-radio {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border-radius: 6px;
  cursor: pointer;
}
.sg-radio-row-stacked .sg-radio:hover {
  border-color: #c9d0d8;
  background: #f8fafc;
}
.sg-radio-row-stacked .sg-radio input[type="radio"] {
  margin-top: 3px;
}
.sg-radio-text {
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-size: 13px;
}
.sg-radio-sub {
  font-size: 12px;
  color: #6b7785;
  font-weight: normal;
  line-height: 1.45;
}
.sg-field-muted { opacity: 0.55; }
.sg-field-muted * { pointer-events: none; }

/* Slice 8d — chip input for notification emails */
.sg-chip-input {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  padding: 6px 8px;
  border: 1px solid #d4d8de;
  border-radius: 6px;
  background: #fff;
  min-height: 40px;
  transition: border-color 0.12s ease, box-shadow 0.12s ease;
}
.sg-chip-input-focused {
  border-color: #0d5f54;
  box-shadow: 0 0 0 3px rgba(13, 95, 84, 0.12);
}
.sg-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: #e6f4f1;
  color: #0d5f54;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 500;
}
.sg-chip-x {
  background: none;
  border: none;
  color: #0d5f54;
  cursor: pointer;
  font-size: 11px;
  padding: 0 2px;
  line-height: 1;
}
.sg-chip-x:hover { color: #0a4a42; }
.sg-chip-input-field {
  flex: 1;
  min-width: 180px;
  padding: 4px 6px;
  font-size: 13px;
  border: none;
  outline: none;
  background: transparent;
}

/* Toggle switch */
.sg-switch-label {
  display: flex !important;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
}
.sg-switch {
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
  flex-shrink: 0;
  margin-top: 1px;
}
.sg-switch input[type="checkbox"] {
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
}
.sg-switch-slider {
  position: absolute;
  inset: 0;
  background: #cbd5e1;
  border-radius: 20px;
  transition: background 0.18s;
  cursor: pointer;
}
.sg-switch-slider::before {
  content: '';
  position: absolute;
  height: 16px;
  width: 16px;
  left: 2px;
  top: 2px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.18s;
  box-shadow: 0 1px 2px rgba(15,23,42,0.12);
}
.sg-switch input:checked + .sg-switch-slider { background: #0d5f54; }
.sg-switch input:checked + .sg-switch-slider::before { transform: translateX(16px); }
.sg-switch-text { display: flex; flex-direction: column; }
.sg-switch-text strong { font-size: 13px; color: #0f172a; font-weight: 600; }
.sg-switch-sub { font-size: 11.5px; color: #64748b; line-height: 1.5; margin-top: 3px; }

/* Save bar */
.sg-savebar {
  position: sticky;
  bottom: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(8px);
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  margin-top: 24px;
  box-shadow: 0 4px 12px rgba(15,23,42,0.05);
}
.sg-dirty {
  font-size: 12px;
  color: #b45309;
  font-weight: 500;
}
.sg-clean {
  font-size: 12px;
  color: #64748b;
}
.sg-spacer { flex: 1; }
.sg-btn,
.sg-btn-primary {
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid #e4e7ec;
  background: #fff;
  color: #1e293b;
}
.sg-btn:hover:not(:disabled) { background: #f1f3f6; }
.sg-btn:disabled,
.sg-btn-primary:disabled { opacity: 0.55; cursor: not-allowed; }
.sg-btn-primary {
  background: #0d5f54;
  border-color: #0d5f54;
  color: #fff;
}
.sg-btn-primary:hover:not(:disabled) { background: #094840; }
</style>
